import React, { useState, useEffect } from 'react';
import './Booking.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaHiking, FaUsers, FaComment, FaAngleDown } from 'react-icons/fa';
import { FaCalendarDays } from 'react-icons/fa6';

import axios from "axios";


const API_PATH = 'https://campfire-react.asdesignsgalaxy.com/api/phpmailer/booking.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const Booking = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    expedition: '',
    departureDate: '',
    numberOfParticipants: '',
    instructions: ''
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.expedition) validationErrors.push('Tour selection is required');
      if (!formData.departureDate) validationErrors.push('Departure date is required');
      if (!formData.numberOfParticipants) validationErrors.push('Number of participants is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        expedition: '',
        departureDate: '',
        numberOfParticipants: '',
        instructions: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
 
  return (
    <section className="custom-form booking linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Book Tour"} subTitle={"Book Your Adventure"} sideHeading linear />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="number"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Your Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Select Tours Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="expedition"><FaHiking /></label>
                  <select
                    className="select-box"
                    id="expedition"
                    name="expedition"
                    value={formData.expedition}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Tours</option>
                    <option value="Mountain_Trek">Mountain Trek</option>
                    <option value="Backcountry_Camping">Backcountry Camping</option>
                    <option value="national_park">National Park Expedition</option>
                    <option value="Forest_Exploration">Forest Exploration</option>
                    <option value="Desert_Safari">Desert Safari</option>
                    <option value="heritage_hike">Cultural Heritage Hike</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

            </div>

            <div className="input-box">

              {/* Departure Date Input Field */}
              <div className="input-field">
                <label htmlFor="departureDate"><FaCalendarDays /></label>
                <input
                  type="text"
                  name="departureDate"
                  className="box"
                  id="departureDate"
                  placeholder="Departure Date"
                  value={formData.departureDate}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Number of Participants Input Field */}
              <div className="input-field">
                <label htmlFor="numberOfParticipants"><FaUsers /></label>
                <input
                  type="number"
                  name="numberOfParticipants"
                  className="box"
                  id="numberOfParticipants"
                  placeholder="Number of Participants"
                  value={formData.numberOfParticipants}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

            </div>

            {/* Additional Instructions Text Area */}
            <div className="input-field text-area">
              <label htmlFor="instructions"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="instructions"
                className="box"
                id="instructions"
                placeholder="Additional Instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Booking Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default Booking;